<template>
    <div>
        <v-container fluid>
            <!-- Page Header -->
            <v-skeleton-loader :loading="!flagNav" transition="scale-transition" max-height="64" type="table-heading">
                <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title text-white">
                    <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">
                        Detailed View
                    </v-toolbar-title>
                    <v-spacer />
                    <div class="d-flex pr-4">
                        <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
                    </div>
                </v-toolbar>
            </v-skeleton-loader>

            <div class="d-flex align-center justify-end mt-4 mb-4">
                <div class="d-flex ga-2 align-center">
                    <!-- Run Selector Dropdown -->
                    <v-select v-model="selectedRunId" :items="selectedJobRuns" item-title="text" item-value="value"
                        variant="outlined" density="compact" hide-details style="min-width: 300px" :loading="loading">
                        <template #prepend-inner>
                            <v-icon size="small" color="primary">mdi-history</v-icon>
                        </template>
                    </v-select>
                    <v-btn color="primary" variant="elevated" @click="exportToExcel" :disabled="!selectedRunData">
                        <v-icon class="mr-1">mdi-microsoft-excel</v-icon>
                        Export to Excel
                    </v-btn>
                </div>
            </div>

            <!-- Loading State -->
            <div v-if="loading && !selectedRunData" class="d-flex align-center justify-center" style="height: 400px;">
                <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            </div>

            <!-- Content -->
            <div v-else-if="selectedRunData">
                <!-- Intent Summary Cards - Show when validationType includes intent -->
                <v-row class="mb-4" v-if="hasIntentValidation">
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" :class="getAccuracyClass(selectedRunData.intentAccuracy)">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ selectedRunData.intentAccuracy?.toFixed(1) || 0 }}%</div>
                                <div class="text-body-2 mt-2">Intent Accuracy</div>
                                <div class="text-caption text-grey mt-1">{{ selectedRunData.correctCount || 0 }}/{{ selectedRunData.totalQuestions }} full match</div>
                                <div class="d-flex justify-center ga-1 mt-2 flex-wrap">
                                    <v-chip size="x-small" variant="tonal" color="grey">L1 {{ selectedRunData.l1Accuracy?.toFixed(0) || 0 }}%</v-chip>
                                    <v-chip size="x-small" variant="tonal" color="secondary">L2 {{ selectedRunData.l2Accuracy?.toFixed(0) || 0 }}%</v-chip>
                                    <v-chip size="x-small" variant="tonal" color="info">L3 {{ selectedRunData.l3Accuracy?.toFixed(0) || 0 }}%</v-chip>
                                    <v-chip size="x-small" variant="tonal" color="primary">L4 {{ selectedRunData.l4Accuracy?.toFixed(0) || 0 }}%</v-chip>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card border-info">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ formatSeconds(selectedRunData.avgTTFC) }}</div>
                                <div class="text-body-2 mt-2">Avg TTFC</div>
                                <div class="text-caption text-grey mt-1">Time to first chunk</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" :class="getFallbackClass(selectedRunData.fallbackRate)">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ selectedRunData.fallbackRate?.toFixed(1) || 0 }}%</div>
                                <div class="text-body-2 mt-2">Fallback Rate</div>
                                <div class="text-caption text-grey mt-1">{{ selectedRunData.fallbackCount || 0 }} fallbacks</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" variant="outlined">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold text-grey-darken-2">{{ selectedRunData.totalQuestions }}</div>
                                <div class="text-body-2 mt-2">Questions Tested</div>
                                <div class="text-caption text-grey mt-1">{{ selectedRunData.totalApiCalls }} API calls (R={{ selectedRunData.reliabilityCount }})</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Answer Summary Cards - Show when validationType includes answer -->
                <v-row class="mb-4" v-if="hasAnswerValidation">
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" :class="getAnswerQualityClass(selectedRunData.answerQuality)">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ selectedRunData.answerQuality || 0 }}%</div>
                                <div class="text-body-2 mt-2">Answer Quality</div>
                                <div class="text-caption text-grey mt-1">LLM-as-Judge</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" :class="getAnswerQualityClass(guidelinePassRate)">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ guidelinePassRate }}%</div>
                                <div class="text-body-2 mt-2">Guideline Pass Rate</div>
                                <div class="text-caption text-grey mt-1">
                                    {{ selectedRunData.guidelinePassCount }}/{{ selectedRunData.guidelineTotalCount }}
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card" :class="getAnswerQualityClass(exactMatchRate)">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ exactMatchRate }}%</div>
                                <div class="text-body-2 mt-2">Exact Match Rate</div>
                                <div class="text-caption text-grey mt-1">
                                    {{ selectedRunData.exactPassCount }}/{{ selectedRunData.exactTotalCount }}
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" sm="6" md="3">
                        <v-card class="summary-card border-info">
                            <v-card-text class="text-center pa-4">
                                <div class="text-h3 font-weight-bold">{{ formatSeconds(selectedRunData.avgTTFC) }}</div>
                                <div class="text-body-2 mt-2">Avg TTFC</div>
                                <div class="text-caption text-grey mt-1">Time to first chunk</div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <v-row class="mb-4">
                    <v-col cols="12" md="6">
                        <v-card variant="outlined" class="h-100">
                            <v-card-title class="text-subtitle-1 pa-3 bg-grey-lighten-4">
                                <v-icon class="mr-2" size="small">mdi-timer-outline</v-icon>
                                Response Time
                            </v-card-title>
                            <v-card-text class="pa-3">
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <span class="text-body-2">Minimum:</span>
                                    <span class="font-weight-medium">{{ formatMs(selectedRunData.ttfcMin)
                                    }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <span class="text-body-2">Maximum:</span>
                                    <span class="font-weight-medium">{{ formatMs(selectedRunData.ttfcMax)
                                    }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center">
                                    <span class="text-body-2">Median:</span>
                                    <span class="font-weight-medium">{{ formatMs(selectedRunData.ttfcMedian)
                                    }}</span>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-card variant="outlined" class="h-100">
                            <v-card-title class="text-subtitle-1 pa-3 bg-grey-lighten-4">
                                <v-icon class="mr-2" size="small">mdi-cog-outline</v-icon>
                                Test Configuration
                            </v-card-title>
                            <v-card-text class="pa-3">
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <span class="text-body-2">Model ID:</span>
                                    <span class="font-weight-medium">{{ selectedRunData.modelId || '-' }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <span class="text-body-2">Judge Model ID:</span>
                                    <span class="font-weight-medium">{{ selectedRunData.judgeModelId || '-' }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center mb-3">
                                    <span class="text-body-2">Total Questions:</span>
                                    <span class="font-weight-medium">{{ selectedRunData.totalQuestions }}</span>
                                </div>
                                <div class="d-flex justify-space-between align-center">
                                    <span class="text-body-2">Total API Calls:</span>
                                    <span class="font-weight-medium">{{ selectedRunData.totalApiCalls }}</span>
                                </div>
                            </v-card-text>
                        </v-card>
                    </v-col>
                </v-row>

                <!-- Intent Summary by Category Table - Show when validationType includes intent -->
                <v-card class="mb-4" variant="outlined" v-if="hasIntentValidation">
                    <v-card-title class="text-subtitle-1 pa-3 bg-grey-lighten-4 d-flex align-center">
                        <v-icon class="mr-2" size="small">mdi-chart-bar</v-icon>
                        Accuracy by Category (Intent Summary)
                    </v-card-title>
                    <v-data-table 
                        :headers="intentCategoryHeaders" 
                        :items="intentCategoryBreakdownWithAverage"
                        :items-per-page="-1" 
                        density="compact" 
                        class="category-summary-table"
                        :hide-default-footer="true">
                        <!-- Category Column -->
                        <template #item.category="{ item }">
                            <div class="d-flex align-center" :class="{ 'font-weight-bold': item.isAverage }">
                                <v-icon v-if="!item.isAverage" size="small" color="primary" class="mr-2">mdi-folder-outline</v-icon>
                                <v-icon v-else size="small" color="grey" class="mr-2">mdi-sigma</v-icon>
                                {{ item.category }}
                            </div>
                        </template>
                        <!-- Accuracy Column -->
                        <template #item.accuracy="{ item }">
                            <v-chip :color="getScoreColor(item.accuracy)" size="x-small" variant="tonal" class="font-weight-medium">
                                {{ item.accuracy?.toFixed(1) }}%
                            </v-chip>
                        </template>
                        <!-- L4+ Accuracy -->
                        <template #item.accuracyL4="{ item }">
                            <span :class="getScoreTextColor(item.accuracyL4)">{{ item.accuracyL4?.toFixed(1) || '0.0' }}%</span>
                        </template>
                        <!-- L3+ Accuracy -->
                        <template #item.accuracyL3="{ item }">
                            <span :class="getScoreTextColor(item.accuracyL3)">{{ item.accuracyL3?.toFixed(1) || '0.0' }}%</span>
                        </template>
                        <!-- L2+ Accuracy -->
                        <template #item.accuracyL2="{ item }">
                            <span :class="getScoreTextColor(item.accuracyL2)">{{ item.accuracyL2?.toFixed(1) || '0.0' }}%</span>
                        </template>
                        <!-- TTFC -->
                        <template #item.ttfc="{ item }">
                            {{ formatMs(item.ttfc) }}
                        </template>
                        <!-- Total Time -->
                        <template #item.totalTime="{ item }">
                            {{ formatMs(item.totalTime) }}
                        </template>
                        <!-- Fallback -->
                        <template #item.fallback="{ item }">
                            <span :class="item.fallback > 10 ? 'text-error' : item.fallback > 5 ? 'text-warning' : 'text-success'">
                                {{ item.fallback?.toFixed(1) || '0.0' }}%
                            </span>
                        </template>
                        <!-- cVal -->
                        <template #item.cVal="{ item }">
                            <span :class="item.cVal > 0 ? 'text-warning' : 'text-success'">{{ item.cVal?.toFixed(1) || '0.0' }}%</span>
                        </template>
                        <!-- sVal -->
                        <template #item.sVal="{ item }">
                            <span :class="item.sVal > 0 ? 'text-warning' : 'text-success'">{{ item.sVal?.toFixed(1) || '0.0' }}%</span>
                        </template>
                    </v-data-table>
                    <div class="px-4 py-2 bg-grey-lighten-5 text-caption">
                        <strong>Remarks:</strong>
                        <ul class="mt-1 mb-0" style="padding-left: 16px;">
                            <li><strong>Acc%:</strong> Original accuracy - requires same # of levels AND all levels match</li>
                            <li><strong>Acc(L4+)%:</strong> Match all levels that exist in expected (flexible)</li>
                            <li><strong>Acc(L3+)%:</strong> Match L1-L3 levels (first 3 levels)</li>
                            <li><strong>Acc(L2+)%:</strong> Match L1-L2 levels (first 2 levels)</li>
                            <li><strong>TTFC(ms):</strong> Time to First Chunk - time until the first response token is received (streaming latency)</li>
                            <li><strong>Total(ms):</strong> Total response time - complete round-trip time from request to full response</li>
                            <li><strong>Fallback%:</strong> Percentage of responses that triggered fallback/default handling</li>
                            <li><strong>cVal%:</strong> Category variance % (lower is better)</li>
                            <li><strong>sVal%:</strong> Score variance % (lower is better)</li>
                        </ul>
                    </div>
                </v-card>

                <!-- Answer Quality Summary by Category Table - Show when validationType includes answer -->
                <v-card class="mb-4" variant="outlined" v-if="hasAnswerValidation">
                    <v-card-title class="text-subtitle-1 pa-3 bg-grey-lighten-4 d-flex align-center">
                        <v-icon class="mr-2" size="small">mdi-message-check-outline</v-icon>
                        Accuracy by Category (Answer Quality Summary)
                        <v-spacer></v-spacer>
                        <v-chip size="small" color="success" variant="tonal">
                            LLM-as-Judge
                        </v-chip>
                    </v-card-title>
                    <v-data-table 
                        :headers="answerQualityCategoryHeaders" 
                        :items="answerQualityCategoryBreakdownWithAverage"
                        :items-per-page="-1" 
                        density="compact" 
                        class="category-summary-table"
                        :hide-default-footer="true">
                        <!-- Category Column -->
                        <template #item.category="{ item }">
                            <div class="d-flex align-center" :class="{ 'font-weight-bold': item.isAverage }">
                                <v-icon v-if="!item.isAverage" size="small" color="success" class="mr-2">mdi-folder-outline</v-icon>
                                <v-icon v-else size="small" color="grey" class="mr-2">mdi-sigma</v-icon>
                                {{ item.category }}
                            </div>
                        </template>
                        <!-- Answer Accuracy Column -->
                        <template #item.answerAccuracy="{ item }">
                            <v-chip :color="getScoreColor(item.answerAccuracy)" size="x-small" variant="tonal" class="font-weight-medium">
                                {{ item.answerAccuracy?.toFixed(1) || '0.0' }}%
                            </v-chip>
                        </template>
                        <!-- Answer Variance -->
                        <template #item.answerVariance="{ item }">
                            <span :class="item.answerVariance > 10 ? 'text-error' : item.answerVariance > 0 ? 'text-warning' : 'text-success'">
                                {{ item.answerVariance?.toFixed(1) || '0.0' }}%
                            </span>
                        </template>
                        <!-- Guideline Pass/Total -->
                        <template #item.guidelinePass="{ item }">
                            <span :class="getGuidelineColor(item.guidelinePassCount, item.guidelineTotalCount)">
                                {{ item.guidelinePassCount || 0 }}/{{ item.guidelineTotalCount || 0 }}
                            </span>
                        </template>
                        <!-- Guideline Variance -->
                        <template #item.guidelineVariance="{ item }">
                            <span :class="item.guidelineVariance > 10 ? 'text-error' : item.guidelineVariance > 0 ? 'text-warning' : 'text-success'">
                                {{ item.guidelineVariance?.toFixed(1) || '0.0' }}%
                            </span>
                        </template>
                        <!-- Exact Pass/Total -->
                        <template #item.exactPass="{ item }">
                            <span :class="getGuidelineColor(item.exactPassCount, item.exactTotalCount)">
                                {{ item.exactPassCount || 0 }}/{{ item.exactTotalCount || 0 }}
                            </span>
                        </template>
                        <!-- Exact Variance -->
                        <template #item.exactVariance="{ item }">
                            <span :class="item.exactVariance > 10 ? 'text-error' : item.exactVariance > 0 ? 'text-warning' : 'text-success'">
                                {{ item.exactVariance?.toFixed(1) || '0.0' }}%
                            </span>
                        </template>
                    </v-data-table>
                    <div class="px-4 py-2 bg-grey-lighten-5 text-caption">
                        <strong>Remarks:</strong>
                        <ul class="mt-1 mb-0" style="padding-left: 16px;">
                            <li><strong>Acc%:</strong> Overall answer accuracy (pass/total API calls)</li>
                            <li><strong>Val%:</strong> Combined answer variance % (lower is better)</li>
                            <li><strong>Guideline:</strong> pass/total - per-call evaluations for guideline type</li>
                            <li><strong>gVal%:</strong> Guideline answer variance % (lower is better)</li>
                            <li><strong>Exact:</strong> pass/total - per-call evaluations for exact type</li>
                            <li><strong>eVal%:</strong> Exact answer variance % (lower is better)</li>
                        </ul>
                    </div>
                </v-card>

                <!-- Excel-Style Detailed Table -->
                <v-card variant="outlined">
                    <v-card-title class="text-subtitle-1 pa-3 bg-grey-lighten-4 d-flex align-center">
                        <v-icon class="mr-2" size="small">mdi-table-large</v-icon>
                        Detailed Question Results (Excel Style)
                        <v-spacer></v-spacer>
                        <v-chip size="small" color="info" variant="tonal">
                            {{ filteredQuestions.length }} questions × {{ maxReliabilityCalls }} calls
                        </v-chip>
                    </v-card-title>

                    <!-- Filter and Search -->
                    <div class="px-3 py-2 bg-grey-lighten-5 d-flex ga-2 flex-wrap align-center">
                        <span class="text-caption text-grey mr-2">Filter:</span>
                        <v-chip v-for="filter in questionFilters" :key="filter.value"
                            :color="selectedQuestionFilter === filter.value ? 'primary' : 'default'"
                            :variant="selectedQuestionFilter === filter.value ? 'flat' : 'outlined'" size="small"
                            @click="selectedQuestionFilter = filter.value">
                            {{ filter.text }} ({{ filter.count }})
                        </v-chip>
                        <v-spacer></v-spacer>
                        <v-text-field v-model="questionSearchQuery" placeholder="Search questions..."
                            prepend-inner-icon="mdi-magnify" variant="outlined" density="compact" hide-details
                            style="max-width: 250px" clearable></v-text-field>
                    </div>

                    <!-- The Excel-Style Table -->
                    <div class="table-wrapper">
                        <v-data-table :headers="excelHeaders" :items="paginatedQuestions" :items-per-page="10"
                            density="comfortable" class="excel-style-table" fixed-header height="600">
                            
                            <!-- No Column -->
                            <template #item.no="{ item }">
                                <div class="font-weight-bold text-center">{{ item.displayNo }}</div>
                            </template>

                            <!-- Question Column -->
                            <template #item.question="{ item }">
                                <div style="max-width: 250px;">
                                    <v-tooltip location="top" max-width="400">
                                        <template #activator="{ props }">
                                            <div v-bind="props" class="text-truncate">{{ item.question }}</div>
                                        </template>
                                        {{ item.question }}
                                    </v-tooltip>
                                </div>
                            </template>

                            <!-- Category Column -->
                            <template #item.category="{ item }">
                                <div>{{ item.category }}</div>
                            </template>

                            <!-- SessionID Column - Grouped -->
                            <template #item.sessionId="{ item }">
                                <div class="grouped-cell">
                                    <div v-for="(call, idx) in (item.reliabilityCalls || [])" :key="idx" class="grouped-line">
                                        <span class="cell-label">Call{{ idx + 1 }}:</span> {{ call.sessionId || item.sessionId || '-' }}
                                    </div>
                                </div>
                            </template>

                            <!-- Intent Column - Grouped (Expected + All Calls) - Show when hasIntentValidation -->
                            <template #item.intent="{ item }">
                                <div class="grouped-cell">
                                    <div class="grouped-line expected-line">
                                        <span class="cell-label">Expected:</span> {{ formatIntentPath(item.expected) }}
                                    </div>
                                    <div v-for="(call, idx) in (item.reliabilityCalls || [])" :key="idx"
                                         class="grouped-line"
                                         :class="getIntentMatchClass(item, idx)">
                                        <span class="cell-label">Call{{ idx + 1 }}:</span> {{ call.intent || '-' }}
                                    </div>
                                </div>
                            </template>

                            <!-- Intent Result Column - Grouped Results -->
                            <template #item.intentResult="{ item }">
                                <div class="grouped-cell text-center">
                                    <div v-for="(call, idx) in (item.reliabilityCalls || [])" :key="idx" class="grouped-line">
                                        <v-chip size="x-small" :color="call.match ? 'success' : 'error'" variant="flat">
                                            {{ call.match ? 'PASS' : 'FAIL' }}
                                        </v-chip>
                                    </div>
                                </div>
                            </template>

                            <!-- Answer Column - Grouped (Expected + All Calls) with See More -->
                            <template #item.answer="{ item }">
                                <div class="grouped-cell">
                                    <!-- Expected Answer -->
                                    <div class="grouped-line expected-line">
                                        <span class="cell-label">Expected:</span>
                                        <span v-if="!expandedCells[`answer-exp-${item.masterId}`] && (item.expectedAnswer || '').length > 150">
                                            {{ item.expectedAnswer.substring(0, 150) }}...
                                            <v-btn size="x-small" variant="text" color="primary" class="ml-1 see-more-btn"
                                                   @click="toggleCell(`answer-exp-${item.masterId}`)">
                                                See more
                                            </v-btn>
                                        </span>
                                        <span v-else>
                                            {{ item.expectedAnswer || '-' }}
                                            <v-btn v-if="(item.expectedAnswer || '').length > 150"
                                                   size="x-small" variant="text" color="primary" class="ml-1 see-more-btn"
                                                   @click="toggleCell(`answer-exp-${item.masterId}`)">
                                                See less
                                            </v-btn>
                                        </span>
                                    </div>
                                    <!-- Call Answers -->
                                    <div v-for="(call, idx) in (item.reliabilityCalls || [])" :key="idx" class="grouped-line">
                                        <span class="cell-label">Call{{ idx + 1 }}:</span>
                                        <span v-if="!expandedCells[`answer-${item.masterId}-${idx}`] && (call.botAnswer || '').length > 150">
                                            {{ call.botAnswer.substring(0, 150) }}...
                                            <v-btn size="x-small" variant="text" color="primary" class="ml-1 see-more-btn"
                                                   @click="toggleCell(`answer-${item.masterId}-${idx}`)">
                                                See more
                                            </v-btn>
                                        </span>
                                        <span v-else>
                                            {{ call.botAnswer || '-' }}
                                            <v-btn v-if="(call.botAnswer || '').length > 150"
                                                   size="x-small" variant="text" color="primary" class="ml-1 see-more-btn"
                                                   @click="toggleCell(`answer-${item.masterId}-${idx}`)">
                                                See less
                                            </v-btn>
                                        </span>
                                    </div>
                                </div>
                            </template>

                            <!-- Answer Eval Column - Grouped Results -->
                            <template #item.answerEval="{ item }">
                                <div class="grouped-cell text-center">
                                    <div v-for="(call, idx) in (item.reliabilityCalls || [])" :key="idx" class="grouped-line">
                                        <span v-if="call.hasAnswerEval">
                                            <v-chip :color="call.answerEvalPass ? 'success' : 'error'" size="x-small" variant="tonal">
                                                Call{{ idx + 1 }}: {{ call.answerEvalPass ? 'PASS' : 'FAIL' }}
                                            </v-chip>
                                        </span>
                                        <span v-else class="text-grey">Call{{ idx + 1 }}: -</span>
                                    </div>
                                </div>
                            </template>

                            <!-- Reason Column - Grouped Evaluation Reasons with See More -->
                            <template #item.reason="{ item }">
                                <div class="grouped-cell">
                                    <div v-for="(call, idx) in (item.reliabilityCalls || [])" :key="idx" class="grouped-line">
                                        <div v-if="call.hasAnswerEval && call.answerEvalReason">
                                            <span class="cell-label">Call{{ idx + 1 }}:</span>
                                            <span v-if="!expandedCells[`reason-${item.masterId}-${idx}`] && (call.answerEvalReason || '').length > 150">
                                                {{ call.answerEvalReason.substring(0, 150) }}...
                                                <v-btn size="x-small" variant="text" color="primary" class="ml-1 see-more-btn"
                                                       @click="toggleCell(`reason-${item.masterId}-${idx}`)">
                                                    See more
                                                </v-btn>
                                            </span>
                                            <span v-else>
                                                {{ call.answerEvalReason }}
                                                <v-btn v-if="(call.answerEvalReason || '').length > 150"
                                                       size="x-small" variant="text" color="primary" class="ml-1 see-more-btn"
                                                       @click="toggleCell(`reason-${item.masterId}-${idx}`)">
                                                    See less
                                                </v-btn>
                                            </span>
                                        </div>
                                        <span v-else class="text-grey">Call{{ idx + 1 }}: -</span>
                                    </div>
                                </div>
                            </template>

                            <!-- Answer Type Column -->
                            <template #item.answerType="{ item }">
                                <v-chip :color="item.answerType === 'exact' ? 'info' : 'success'" size="x-small" variant="tonal">
                                    {{ item.answerType || 'guideline' }}
                                </v-chip>
                            </template>

                            <!-- Variance Column -->
                            <template #item.variance="{ item }">
                                <div class="text-center">
                                    <v-chip :color="getVarianceColor(item)" size="x-small" variant="tonal">
                                        {{ getVarianceText(item) }}
                                    </v-chip>
                                </div>
                            </template>
                        </v-data-table>
                    </div>
                </v-card>
            </div>
        </v-container>
    </div>
</template>

<script>
export default {
    name: 'ValidationJobDetailV2',
    inject: ["flagNav", "FontColorTitle", "SubNavbarBackGround"],
    computed: {
        // Check if this job has intent validation
        hasIntentValidation() {
            const vt = this.selectedRunData?.validationType;
            return vt === 'intent' || vt === 'both';
        },

        // Check if this job has answer validation
        hasAnswerValidation() {
            const vt = this.selectedRunData?.validationType;
            return vt === 'answer' || vt === 'both';
        },

        // Calculate guideline pass rate percentage
        guidelinePassRate() {
            if (!this.selectedRunData || !this.selectedRunData.guidelineTotalCount) return 0;
            return Math.round((this.selectedRunData.guidelinePassCount / this.selectedRunData.guidelineTotalCount) * 100);
        },

        // Calculate exact match rate percentage
        exactMatchRate() {
            if (!this.selectedRunData || !this.selectedRunData.exactTotalCount) return 0;
            return Math.round((this.selectedRunData.exactPassCount / this.selectedRunData.exactTotalCount) * 100);
        },

        // Intent Category Summary Headers
        intentCategoryHeaders() {
            return [
                { title: 'Category', key: 'category', width: '200px' },
                { title: 'N', key: 'n', width: '50px', align: 'center' },
                { title: 'R', key: 'reliabilityCount', width: '50px', align: 'center' },
                { title: 'API', key: 'apiCalls', width: '60px', align: 'center' },
                { title: 'Acc%', key: 'accuracy', width: '80px', align: 'center' },
                { title: 'Acc(L4+)%', key: 'accuracyL4', width: '90px', align: 'center' },
                { title: 'Acc(L3+)%', key: 'accuracyL3', width: '90px', align: 'center' },
                { title: 'Acc(L2+)%', key: 'accuracyL2', width: '90px', align: 'center' },
                { title: 'TTFC(ms)', key: 'ttfc', width: '90px', align: 'center' },
                { title: 'Total(ms)', key: 'totalTime', width: '90px', align: 'center' },
                { title: 'Fallback%', key: 'fallback', width: '90px', align: 'center' },
                { title: 'cVal%', key: 'cVal', width: '70px', align: 'center' },
                { title: 'sVal%', key: 'sVal', width: '70px', align: 'center' },
            ];
        },

        // Answer Quality Category Summary Headers
        answerQualityCategoryHeaders() {
            return [
                { title: 'Category', key: 'category', width: '200px' },
                { title: 'N', key: 'n', width: '50px', align: 'center' },
                { title: 'R', key: 'reliabilityCount', width: '50px', align: 'center' },
                { title: 'API', key: 'apiCalls', width: '60px', align: 'center' },
                { title: 'Acc%', key: 'answerAccuracy', width: '80px', align: 'center' },
                { title: 'Val%', key: 'answerVariance', width: '80px', align: 'center' },
                { title: 'Guideline', key: 'guidelinePass', width: '100px', align: 'center' },
                { title: 'gVal%', key: 'guidelineVariance', width: '80px', align: 'center' },
                { title: 'Exact', key: 'exactPass', width: '100px', align: 'center' },
                { title: 'eVal%', key: 'exactVariance', width: '80px', align: 'center' },
            ];
        },

        // Answer Quality Category Breakdown with Average Row
        answerQualityCategoryBreakdownWithAverage() {
            const categories = this.selectedRunData?.categoryBreakdown || [];
            if (categories.length === 0) return [];

            // Map categories with calculated answer quality metrics
            const mappedCategories = categories.map(cat => {
                const answerTotal = cat.answerTotalCount || 0;
                const answerPass = cat.answerPassCount || 0;
                const answerAccuracy = answerTotal > 0 ? (answerPass / answerTotal) * 100 : 0;
                
                return {
                    ...cat,
                    answerAccuracy: answerAccuracy,
                    answerVariance: 0, // TODO: Calculate from detailed results if needed
                    guidelineVariance: 0,
                    exactVariance: 0,
                };
            });

            // Calculate averages
            const totalN = mappedCategories.reduce((sum, cat) => sum + (cat.n || 0), 0);
            const totalApiCalls = mappedCategories.reduce((sum, cat) => sum + (cat.apiCalls || 0), 0);
            const avgReliability = mappedCategories.length > 0 ? mappedCategories[0].reliabilityCount : 1;
            
            const totalAnswerPass = mappedCategories.reduce((sum, cat) => sum + (cat.answerPassCount || 0), 0);
            const totalAnswerTotal = mappedCategories.reduce((sum, cat) => sum + (cat.answerTotalCount || 0), 0);
            const avgAnswerAccuracy = totalAnswerTotal > 0 ? (totalAnswerPass / totalAnswerTotal) * 100 : 0;
            
            const totalGuidelinePass = mappedCategories.reduce((sum, cat) => sum + (cat.guidelinePassCount || 0), 0);
            const totalGuidelineTotal = mappedCategories.reduce((sum, cat) => sum + (cat.guidelineTotalCount || 0), 0);
            
            const totalExactPass = mappedCategories.reduce((sum, cat) => sum + (cat.exactPassCount || 0), 0);
            const totalExactTotal = mappedCategories.reduce((sum, cat) => sum + (cat.exactTotalCount || 0), 0);
            
            const avgAnswerVariance = mappedCategories.length > 0 
                ? mappedCategories.reduce((sum, cat) => sum + (cat.answerVariance || 0), 0) / mappedCategories.length 
                : 0;
            const avgGuidelineVariance = mappedCategories.length > 0 
                ? mappedCategories.reduce((sum, cat) => sum + (cat.guidelineVariance || 0), 0) / mappedCategories.length 
                : 0;
            const avgExactVariance = mappedCategories.length > 0 
                ? mappedCategories.reduce((sum, cat) => sum + (cat.exactVariance || 0), 0) / mappedCategories.length 
                : 0;

            const averageRow = {
                category: 'AVERAGE',
                n: totalN,
                reliabilityCount: avgReliability,
                apiCalls: totalApiCalls,
                answerAccuracy: avgAnswerAccuracy,
                answerVariance: avgAnswerVariance,
                guidelinePassCount: totalGuidelinePass,
                guidelineTotalCount: totalGuidelineTotal,
                guidelineVariance: avgGuidelineVariance,
                exactPassCount: totalExactPass,
                exactTotalCount: totalExactTotal,
                exactVariance: avgExactVariance,
                isAverage: true,
            };

            return [...mappedCategories, averageRow];
        },

        // Intent Category Breakdown with Average Row (for intent/both validation types)
        intentCategoryBreakdownWithAverage() {
            const categories = this.selectedRunData?.categoryBreakdown || [];
            if (categories.length === 0) return [];

            // Calculate averages
            const totalN = categories.reduce((sum, cat) => sum + (cat.n || 0), 0);
            const totalApiCalls = categories.reduce((sum, cat) => sum + (cat.apiCalls || 0), 0);
            const avgReliability = categories.length > 0 ? categories[0].reliabilityCount : 1;
            
            const avgAccuracy = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.accuracy || cat.intentAccuracy || 0), 0) / categories.length 
                : 0;
            const avgAccuracyL4 = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.accuracyL4 || 0), 0) / categories.length 
                : 0;
            const avgAccuracyL3 = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.accuracyL3 || 0), 0) / categories.length 
                : 0;
            const avgAccuracyL2 = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.accuracyL2 || 0), 0) / categories.length 
                : 0;
            
            const catsWithTtfc = categories.filter(cat => cat.ttfc != null);
            const avgTtfc = catsWithTtfc.length > 0 
                ? catsWithTtfc.reduce((sum, cat) => sum + cat.ttfc, 0) / catsWithTtfc.length 
                : null;
            
            const catsWithTotalTime = categories.filter(cat => cat.totalTime != null);
            const avgTotalTime = catsWithTotalTime.length > 0 
                ? catsWithTotalTime.reduce((sum, cat) => sum + cat.totalTime, 0) / catsWithTotalTime.length 
                : null;
            
            const avgFallback = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.fallback || cat.fallbackRate || 0), 0) / categories.length 
                : 0;
            const avgCVal = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.cVal || 0), 0) / categories.length 
                : 0;
            const avgSVal = categories.length > 0 
                ? categories.reduce((sum, cat) => sum + (cat.sVal || 0), 0) / categories.length 
                : 0;

            // Map categories for display
            const mappedIntentCategories = categories.map(cat => ({
                ...cat,
                accuracy: cat.accuracy || cat.intentAccuracy || 0,
                fallback: cat.fallback || cat.fallbackRate || 0,
            }));

            const averageRow = {
                category: 'AVERAGE',
                n: totalN,
                reliabilityCount: avgReliability,
                apiCalls: totalApiCalls,
                accuracy: avgAccuracy,
                accuracyL4: avgAccuracyL4,
                accuracyL3: avgAccuracyL3,
                accuracyL2: avgAccuracyL2,
                ttfc: avgTtfc,
                totalTime: avgTotalTime,
                fallback: avgFallback,
                cVal: avgCVal,
                sVal: avgSVal,
                isAverage: true,
            };

            return [...mappedIntentCategories, averageRow];
        },

        // Excel Headers - Dynamic based on validation type
        excelHeaders() {
            const baseHeaders = [
                { title: 'No', key: 'no', width: '60px', sortable: false },
                { title: 'Question', key: 'question', width: '250px', sortable: false },
                { title: 'Category', key: 'category', width: '150px', sortable: false },
                { title: 'SessionID', key: 'sessionId', width: '350px', sortable: false },
            ];

            // Add Intent columns if hasIntentValidation
            if (this.hasIntentValidation) {
                baseHeaders.push(
                    { title: 'Intent', key: 'intent', width: '450px', sortable: false },
                    { title: 'Intent Result', key: 'intentResult', width: '120px', sortable: false }
                );
            }

            // Add Answer columns if hasAnswerValidation
            if (this.hasAnswerValidation) {
                baseHeaders.push(
                    { title: 'Answer', key: 'answer', width: '600px', sortable: false },
                    { title: 'Answer Eval', key: 'answerEval', width: '120px', sortable: false },
                    { title: 'Reason', key: 'reason', width: '500px', sortable: false },
                    { title: 'Type', key: 'answerType', width: '100px', sortable: false }
                );
            }

            baseHeaders.push({ title: 'Variance', key: 'variance', width: '100px', sortable: false });

            return baseHeaders;
        },

        maxReliabilityCalls() {
            if (!this.detailResults || this.detailResults.length === 0) return 1;
            return Math.max(...this.detailResults.map(q => (q.reliabilityCalls || []).length), 1);
        },

        questionFilters() {
            const all = this.detailResults.length;
            const passed = this.detailResults.filter(q => q.answerPassed).length;
            const failed = this.detailResults.filter(q => q.answerFailed).length;
            const variance = this.detailResults.filter(q => !q.answerConsistent).length;

            return [
                { text: 'All', value: 'all', count: all },
                { text: 'Passed', value: 'passed', count: passed },
                { text: 'Failed', value: 'failed', count: failed },
                { text: 'Has Variance', value: 'variance', count: variance },
            ];
        },

        filteredQuestions() {
            let questions = [...this.detailResults];

            // Apply filter
            if (this.selectedQuestionFilter === 'passed') {
                questions = questions.filter(q => q.answerPassed);
            } else if (this.selectedQuestionFilter === 'failed') {
                questions = questions.filter(q => q.answerFailed);
            } else if (this.selectedQuestionFilter === 'variance') {
                questions = questions.filter(q => !q.answerConsistent);
            }

            // Apply search
            if (this.questionSearchQuery && this.questionSearchQuery.trim()) {
                const query = this.questionSearchQuery.toLowerCase();
                questions = questions.filter(q =>
                    (q.question || '').toLowerCase().includes(query) ||
                    (q.category || '').toLowerCase().includes(query)
                );
            }

            return questions;
        },

        paginatedQuestions() {
            return this.filteredQuestions.map((item, index) => ({
                ...item,
                displayNo: index + 1,
                _uniqueId: `q-${item.masterId}-${index}`,
            }));
        },

        selectedJobRuns() {
            if (!this.selectedJob || !this.selectedJob.runs) return [];
            return this.selectedJob.runs.map(run => ({
                value: run.id,
                text: `Run #${run.runNumber} - ${run.answerQuality || 0}% - ${new Date(run.completedAt).toLocaleDateString()}`,
            }));
        },
    },

    data() {
        return {
            loading: false,
            selectedRunId: null,
            selectedRunData: null,
            detailResults: [],
            questionSearchQuery: '',
            selectedQuestionFilter: 'all',
            today: new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
            rootJobId: null,
            runHistoryData: [],
            selectedJob: null,
            expandedCells: {}, // Track which cells are expanded
        };
    },

    created() {
        this.rootJobId = this.$route.query.id;
        if (this.rootJobId) {
            this.loadJobInfo();
        }
    },

    watch: {
        selectedRunId(newVal) {
            if (newVal) {
                this.loadRunData(newVal);
            }
        },
    },

    methods: {
        loadJobInfo() {
            this.loading = true;

            // Load run history to get all runs for this root job
            this.loadRunHistory(this.rootJobId);
        },

        loadRunHistory(rootJobId) {
            // V2 API: Use GetValidationJobRunHistory
            this.$call.GetValidationJobRunHistory(
                rootJobId,
                (response) => {
                    console.log('=== GetValidationJobRunHistory Response ===', response);
                    if (response.status === 0 && response.data) {
                        this.runHistoryData = (response.data || []).map(run => {
                            console.log('Run data:', run);
                            return {
                                id: run.id,
                                jobId: run.jobId || run.jobID,
                                jobName: run.jobName,
                                runNumber: run.runNumber || 1,
                                validationType: run.validationType,  // Capture validationType from run history
                                accuracy: run.accuracy || run.intentAccuracy,
                                answerQuality: run.answerQuality || run.answerAccuracy || 0,
                                avgTTFC: run.avgTTFC || run.averageTTFC,
                                completedAt: run.completedAt,
                                linkedJobId: run.linkedJobId,
                                modelId: run.modelId,
                                modelName: run.modelName,
                                judgeModelId: run.judgeModelId,
                            };
                        }).sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt)); // Sort by most recent first

                        // Set selectedJob from the first run (all runs share the same job name)
                        if (this.runHistoryData.length > 0) {
                            const firstRun = this.runHistoryData[0];
                            // Strip rerun suffix from job name
                            const baseName = (firstRun.jobName || 'Unnamed Job').replace(/ \(Rerun(?: #\d+)?\)$/, '');
                            this.selectedJob = {
                                rootJobId: rootJobId,
                                jobName: baseName,
                                validationType: firstRun.validationType,  // Store validationType on job
                                runs: this.runHistoryData,
                            };
                            console.log('Selected Job validationType:', firstRun.validationType);

                            // Select the latest run by default (first in the sorted list)
                            this.selectedRunId = this.runHistoryData[0].id;
                        } else {
                            this.loading = false;
                        }
                    } else {
                        this.loading = false;
                    }
                },
                (error) => {
                    console.error('Failed to load run history:', error);
                    this.loading = false;
                }
            );
        },

        loadRunData(runId) {
            let run = this.runHistoryData.find(r => r.id === runId);
            if (!run) {
                run = this.selectedJob?.runs?.find(r => r.id === runId);
            }
            if (!run) return;

            this.selectedRunData = null;
            this.loading = true;

            // V2 API: Use GetValidationJobSummary
            this.$call.GetValidationJobSummary(
                run.jobId,
                (response) => {
                    console.log('=== GetValidationJobSummary Response ===', response);
                    if (response.status === 0 && response.data) {
                        const data = response.data;
                        console.log('Summary data.validationType:', data.validationType);
                        this.selectedRunData = {
                            // Validation type
                            validationType: data.validationType || run.validationType || 'answer',
                            
                            // Intent fields (for intent/both validation types)
                            intentAccuracy: data.intentAccuracy,
                            l1Accuracy: data.l1Accuracy,
                            l2Accuracy: data.l2Accuracy,
                            l3Accuracy: data.l3Accuracy,
                            l4Accuracy: data.l4Accuracy,
                            correctCount: data.correctCount || 0,
                            fallbackRate: data.fallbackRate || 0,
                            fallbackCount: data.fallbackCount || 0,
                            
                            // Answer quality fields
                            avgTTFC: data.avgTTFC || data.averageTTFC || null,
                            answerQuality: data.answerQuality || data.answerAccuracy || 0,
                            answerPassCount: data.answerPassCount || 0,
                            answerEvalCount: data.answerEvalCount || 0,
                            totalApiCalls: data.totalApiCalls || data.processedQuestions || 0,
                            reliabilityCount: data.reliability || 1,
                            totalQuestions: data.totalQuestions || 0,
                            ttfcMin: data.ttfcMin || null,
                            ttfcMax: data.ttfcMax || null,
                            ttfcMedian: data.ttfcMedian || null,
                            avgResponseTime: data.avgResponseTime || null,
                            guidelinePassCount: data.guidelinePassCount || 0,
                            guidelineTotalCount: data.guidelineTotalCount || 0,
                            exactPassCount: data.exactPassCount || 0,
                            exactTotalCount: data.exactTotalCount || 0,
                            modelId: run.modelId || null,
                            judgeModelId: run.judgeModelId || null,
                            modelName: data.modelName || run.modelName || '-',
                            categoryBreakdown: (data.categoryBreakdown || []).map(cat => ({
                                category: cat.category || 'Uncategorized',
                                n: cat.n || cat.total || 0,
                                reliabilityCount: cat.reliability || data.reliability || 1,
                                apiCalls: cat.apiCalls || (cat.n || cat.total || 0) * (cat.reliability || data.reliability || 1),
                                // Intent fields
                                intentMatchCount: cat.intentMatchCount || 0,
                                intentAccuracy: cat.intentAccuracy,
                                // Answer quality fields
                                answerPassCount: cat.answerPassCount || 0,
                                answerTotalCount: cat.answerTotalCount || 0,
                                guidelinePassCount: cat.guidelinePassCount || 0,
                                guidelineTotalCount: cat.guidelineTotalCount || 0,
                                exactPassCount: cat.exactPassCount || 0,
                                exactTotalCount: cat.exactTotalCount || 0,
                                // Performance fields - map API field names to frontend field names
                                ttfc: cat.avgTTFC || cat.avgTtfc || null,
                                totalTime: cat.avgResponseTime || null,
                                fallback: cat.fallbackRate || 0,
                            })),
                        };

                        // Load detail results
                        this.loadDetailResults(run.jobId);
                    }
                    this.loading = false;
                },
                (error) => {
                    console.error('Failed to load run data:', error);
                    this.loading = false;
                }
            );
        },

        loadDetailResults(jobId) {
            // V2 API: Use GetTableValidationResults
            this.$call.GetTableValidationResults(
                { JobId: jobId, PerPage: 5000 },
                (response) => {
                    if (response.status === 0 && response.data) {
                        const items = response.data.data || response.data.items || response.data || [];
                        this.detailResults = this.groupResultsByQuestion(items);
                    }
                },
                (error) => {
                    console.error('Failed to load detail results:', error);
                }
            );
        },

        groupResultsByQuestion(flatResults) {
            const grouped = {};

            flatResults.forEach(result => {
                // FIX: Use questionId (V2 API) instead of masterId (V1 API)
                const key = result.questionId || result.masterId;
                if (!grouped[key]) {
                    grouped[key] = {
                        masterId: result.questionId || result.masterId,
                        sessionId: result.sessionId,
                        question: result.question,
                        category: result.category,
                        reliabilityCalls: [],
                        answerEvals: [],
                        answerType: result.answerType || 'guideline',
                        // FIX: Use expectedAnswer (V2 API) instead of answerGuide (V1 API)
                        expectedAnswer: result.expectedAnswer || result.answerGuide || '',
                        latestBotAnswer: null,
                        // FIX: Intent fields - use V2 API field names (expectedIntentL1, etc.)
                        expected: {
                            L1: result.expectedIntentL1 || result.level1 || result.expectedLevel1 || '',
                            L2: result.expectedIntentL2 || result.level2 || result.expectedLevel2 || '',
                            L3: result.expectedIntentL3 || result.level3 || result.expectedLevel3 || '',
                            L4: result.expectedIntentL4 || result.level4 || result.expectedLevel4 || '',
                        },
                    };
                }

                grouped[key].reliabilityCalls.push({
                    // FIX: Use callNumber (V2 API) instead of reliabilityCallNumber (V1 API)
                    callNumber: result.callNumber || result.reliabilityCallNumber || 1,
                    sessionId: result.sessionId || '',
                    // FIX: Use actualAnswer (V2 API) instead of botAnswer (V1 API)
                    botAnswer: result.actualAnswer || result.botAnswer || '',
                    ttfc: result.ttfcMs || result.ttfc || 0,
                    // FIX: Intent fields - use V2 API field names
                    intent: result.actualIntentFull || result.returnIntent || result.intent || '',
                    match: result.isFullMatch !== undefined ? result.isFullMatch : (result.match !== undefined ? result.match : result.intentMatch),
                    confidence: result.confidenceL4 || result.confidenceL3 || result.confidenceL2 || result.confidenceL1,
                    // Answer evaluation for this specific call
                    hasAnswerEval: result.answerEvalPass !== null && result.answerEvalPass !== undefined,
                    answerEvalPass: result.answerEvalPass,
                    answerEvalReason: result.answerEvalReason || '',
                });

                // FIX: Use actualAnswer (V2 API) instead of botAnswer (V1 API)
                if (result.actualAnswer || result.botAnswer) {
                    grouped[key].latestBotAnswer = result.actualAnswer || result.botAnswer;
                }

                if (result.answerEvalPass !== null && result.answerEvalPass !== undefined) {
                    grouped[key].answerEvals.push({
                        pass: result.answerEvalPass,
                        reason: result.answerEvalReason || '',
                        // FIX: Use actualAnswer (V2 API) instead of botAnswer (V1 API)
                        botAnswer: result.actualAnswer || result.botAnswer || '',
                    });
                }
            });

            return Object.values(grouped).map((item, index) => {
                const calls = item.reliabilityCalls;

                // Check answer evaluation consistency (only if evaluations exist)
                const callsWithEval = calls.filter(c => c.hasAnswerEval);
                let answerConsistent = true;
                let answerPassed = false;
                let answerFailed = false;

                if (callsWithEval.length > 0) {
                    // Check if all calls passed or all failed
                    const allPassed = callsWithEval.every(c => c.answerEvalPass === true);
                    const allFailed = callsWithEval.every(c => c.answerEvalPass === false);
                    
                    answerPassed = allPassed;
                    answerFailed = allFailed;
                    
                    if (callsWithEval.length > 1) {
                        // All calls should have same Pass/Fail result
                        const firstEvalResult = callsWithEval[0].answerEvalPass;
                        answerConsistent = callsWithEval.every(c => c.answerEvalPass === firstEvalResult);
                    }
                }

                return {
                    ...item,
                    no: index + 1,
                    answerConsistent: answerConsistent,
                    answerPassed: answerPassed,
                    answerFailed: answerFailed,
                };
            });
        },

        getVarianceColor(item) {
            if (item.answerConsistent) return 'success';
            return 'warning';
        },

        getVarianceText(item) {
            if (item.answerConsistent) return 'Consistent';
            return 'Variance';
        },

        toggleCell(cellKey) {
            this.expandedCells[cellKey] = !this.expandedCells[cellKey];
            // Force Vue to update the reactive object
            this.expandedCells = { ...this.expandedCells };
        },

        formatMs(ms) {
            if (!ms) return '0.0s';
            return (ms / 1000).toFixed(1) + 's';
        },

        formatSeconds(ms) {
            if (!ms) return '0.0s';
            return (ms / 1000).toFixed(1) + 's';
        },

        truncateText(text, maxLength) {
            if (!text) return '-';
            if (text.length <= maxLength) return text;
            return text.substring(0, maxLength) + '...';
        },

        getAnswerQualityClass(quality) {
            if (quality >= 90) return 'border-success';
            if (quality >= 70) return 'border-warning';
            return 'border-error';
        },

        getAccuracyClass(accuracy) {
            if (accuracy >= 90) return 'border-success';
            if (accuracy >= 70) return 'border-warning';
            return 'border-error';
        },

        getFallbackClass(rate) {
            if (rate <= 5) return 'border-success';
            if (rate <= 15) return 'border-warning';
            return 'border-error';
        },

        // Helper methods for category summary tables
        getScoreColor(score) {
            if (score >= 90) return 'success';
            if (score >= 70) return 'warning';
            return 'error';
        },

        getScoreTextColor(score) {
            if (score >= 90) return 'text-success';
            if (score >= 70) return 'text-warning';
            return 'text-error';
        },

        getGuidelineColor(pass, total) {
            if (total === 0) return 'text-grey';
            const rate = (pass / total) * 100;
            if (rate >= 90) return 'text-success';
            if (rate >= 70) return 'text-warning';
            return 'text-error';
        },

        exportToExcel() {
            // TODO: Implement Excel export functionality
            alert('Excel export functionality to be implemented');
        },

        // Intent-related helper methods
        formatIntentPath(expected) {
            if (!expected) return '-';
            const parts = [];
            // FIX: Use L1, L2, L3, L4 (V2 API) instead of level1, level2, etc. (V1 API)
            if (expected.L1) parts.push(expected.L1);
            if (expected.L2) parts.push(expected.L2);
            if (expected.L3) parts.push(expected.L3);
            if (expected.L4) parts.push(expected.L4);
            return parts.length > 0 ? parts.join(' > ') : '-';
        },

        getIntentMatchClass(item, callIndex) {
            const call = item.reliabilityCalls?.[callIndex];
            if (!call) return '';
            return call.match ? 'match-success' : 'match-fail';
        },
    },
};
</script>

<style scoped>
.summary-card {
    border-width: 2px !important;
    transition: transform 0.2s, box-shadow 0.2s;
    min-height: 164px !important;
}

.summary-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.border-success {
    border-color: rgb(var(--v-theme-success)) !important;
}

.border-warning {
    border-color: rgb(var(--v-theme-warning)) !important;
}

.border-error {
    border-color: rgb(var(--v-theme-error)) !important;
}

.border-info {
    border-color: rgb(var(--v-theme-info)) !important;
}

.table-wrapper {
    overflow-x: auto;
}

.excel-style-table :deep(table) {
    table-layout: auto !important;
    width: max-content !important;
}

.excel-style-table :deep(th) {
    background-color: #4caf50 !important;
    color: white !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    white-space: nowrap !important;
    border-right: 1px solid rgba(255, 255, 255, 0.3) !important;
}

.excel-style-table :deep(td) {
    font-size: 11px !important;
    border-right: 1px solid #e0e0e0 !important;
    border-bottom: 1px solid #e0e0e0 !important;
    padding: 12px !important;
    vertical-align: top !important;
}

/* Force column widths for Answer columns */
.excel-style-table :deep(th:nth-child(4)),
.excel-style-table :deep(td:nth-child(4)) {
    min-width: 350px !important;
    max-width: 350px !important;
    width: 350px !important;
}

.excel-style-table :deep(th:nth-child(5)),
.excel-style-table :deep(td:nth-child(5)) {
    min-width: 600px !important;
    max-width: 600px !important;
    width: 600px !important;
}

.excel-style-table :deep(th:nth-child(7)),
.excel-style-table :deep(td:nth-child(7)) {
    min-width: 500px !important;
    max-width: 500px !important;
    width: 500px !important;
}

/* Grouped Cell Styling */
.grouped-cell {
    font-size: 11px;
    line-height: 1.5;
    word-wrap: break-word;
    white-space: normal;
}

.grouped-line {
    padding: 3px 0;
    border-bottom: 1px solid #f0f0f0;
}

.grouped-line:last-child {
    border-bottom: none;
}

.expected-line {
    font-weight: 600;
    background-color: #e8f5e9;
    padding: 4px;
    margin-bottom: 4px;
    border-radius: 3px;
}

.cell-label {
    font-weight: 600;
    color: #666;
    min-width: 40px;
    display: inline-block;
}

/* See More Button */
.see-more-btn {
    min-width: auto !important;
    padding: 0 8px !important;
    height: 20px !important;
    font-size: 10px !important;
    text-transform: none !important;
    letter-spacing: normal !important;
}

.see-more-btn :deep(.v-btn__content) {
    font-size: 10px !important;
}

/* Category Summary Table Styling */
.category-summary-table :deep(th) {
    background-color: #f5f5f5 !important;
    font-weight: 600 !important;
    font-size: 12px !important;
    white-space: nowrap !important;
}

.category-summary-table :deep(td) {
    font-size: 12px !important;
    border-bottom: 1px solid #e0e0e0 !important;
}

.category-summary-table :deep(tr:last-child td) {
    background-color: #eeeeee !important;
    font-weight: 600 !important;
    border-top: 1px solid #bdbdbd !important;
}

.cursor-pointer {
    cursor: pointer;
}

/* Intent Match Styling */
.match-success {
    background-color: #e8f5e9;
    color: #2e7d32;
}

.match-fail {
    background-color: #ffebee;
    color: #c62828;
}
</style>
